// SPDX-License-Identifier: GPL-3.0-or-later
//
// Post-wallet flourish on the dashboard: once AI is connected, let the user
// screen saved jobs against their profile and batch-generate documents across
// several jobs at once. Dismissible; reuses screen.ts + generate.ts.

import { generateCover, generateCv } from '../generate';
import { autoScreen } from '../screen';
import type { Application } from '../types';
import { el } from './dom';

const DISMISS_KEY = 'applify:onboarding:bulkDismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}
function setDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* private mode */
  }
}

function hasGenerated(a: Application): boolean {
  return !!(a.generated && (a.generated.cv || a.generated.cover || a.generated.interview));
}

export function renderBulkActions(apps: Application[]): HTMLElement | null {
  if (isDismissed() || apps.length === 0) return null;

  const container = el('div', { class: 'bulk-actions card padded-sm' });
  const status = el('div', {
    class: 'bulk-status text-muted',
    role: 'status',
    style: 'font-size: 0.78rem; min-height: 1.2em;',
  });
  const errorBox = el('div', {
    class: 'bulk-errors',
    style: 'font-size: 0.75rem; color: var(--status-rejected); display: flex; flex-direction: column; gap: 0.25rem;',
  });

  const screenTargets = apps.filter((a) => a.status === 'Saved');
  const screenBtn = el(
    'button',
    {
      class: 'btn compact',
      disabled: screenTargets.length === 0 ? true : null,
      onclick: async () => {
        screenBtn.setAttribute('disabled', 'true');
        let ran = 0;
        let filtered = 0;
        for (let i = 0; i < screenTargets.length; i++) {
          status.textContent = `Screening ${i + 1}/${screenTargets.length}…`;
          try {
            const out = await autoScreen(screenTargets[i]!);
            if (out.ran) {
              ran++;
              if (out.result && !out.result.match) filtered++;
            }
          } catch {
            /* best effort */
          }
        }
        status.textContent =
          ran === 0
            ? 'Add job preferences in Settings → Profile to screen jobs.'
            : `Screened ${ran} job${ran === 1 ? '' : 's'} · ${filtered} filtered out.`;
        screenBtn.removeAttribute('disabled');
      },
    },
    `Screen ${screenTargets.length} saved job${screenTargets.length === 1 ? '' : 's'}`,
  );

  // Only jobs you'd actually apply to — skip Filtered/Discarded/Rejected/Closed,
  // where generating a CV and cover letter is pointless.
  const genApps = apps.filter(
    (a) =>
      a.status === 'Saved' ||
      a.status === 'Applied' ||
      a.status === 'Interview' ||
      a.status === 'Offer',
  );

  const checkboxes = new Map<string, HTMLInputElement>();
  const list = el('div', { class: 'bulk-list' });
  if (genApps.length === 0) {
    list.appendChild(
      el(
        'div',
        { class: 'text-muted', style: 'font-size: 0.78rem; padding: 0.35rem 0.4rem;' },
        'No active jobs to generate for.',
      ),
    );
  } else {
    for (const a of genApps) {
      const cb = el('input', { type: 'checkbox' }) as HTMLInputElement;
      checkboxes.set(a.id, cb);
      list.appendChild(
        el(
          'label',
          { class: 'bulk-row' },
          cb,
          el('span', { class: 'bulk-row-title' }, a.jobTitle),
          el('span', { class: 'bulk-row-co text-muted' }, a.companyName),
          hasGenerated(a)
            ? el(
                'span',
                { class: 'step-done', title: 'A CV and/or cover letter has already been generated for this job' },
                '✓ generated',
              )
            : null,
        ),
      );
    }
  }

  const genBtn = el(
    'button',
    {
      class: 'btn primary compact',
      disabled: genApps.length === 0 ? true : null,
      onclick: async () => {
        const selected = genApps.filter((a) => checkboxes.get(a.id)?.checked);
        if (selected.length === 0) {
          status.textContent = 'Select at least one job first.';
          return;
        }
        genBtn.setAttribute('disabled', 'true');
        errorBox.replaceChildren();
        let ok = 0;
        const failures: string[] = [];
        const kinds = [
          ['CV', generateCv],
          ['cover letter', generateCover],
        ] as const;
        for (let i = 0; i < selected.length; i++) {
          const a = selected[i]!;
          for (const [label, fn] of kinds) {
            status.textContent = `Generating ${label} for ${a.jobTitle} (${i + 1}/${selected.length})…`;
            try {
              const out = await fn(a);
              if (out.ok) ok++;
              else failures.push(`${a.jobTitle} — ${label}: ${out.message}`);
            } catch (err) {
              failures.push(`${a.jobTitle} — ${label}: ${err instanceof Error ? err.message : String(err)}`);
            }
          }
        }
        if (failures.length === 0) {
          status.textContent = `Done — ${ok} document${ok === 1 ? '' : 's'} generated. 🎉`;
        } else if (ok > 0) {
          status.textContent = `Generated ${ok}, ${failures.length} failed:`;
        } else {
          status.textContent = `Couldn't generate ${failures.length} document${failures.length === 1 ? '' : 's'}:`;
        }
        errorBox.replaceChildren(...failures.map((f) => el('div', {}, f)));
        genBtn.removeAttribute('disabled');
      },
    },
    'Generate CV + cover for selected',
  );

  container.append(
    el(
      'div',
      { class: 'bulk-head' },
      el('div', { class: 'eyebrow' }, 'Put AI to work'),
      el(
        'button',
        {
          class: 'jw-dismiss',
          onclick: () => {
            setDismissed();
            container.remove();
          },
        },
        'Dismiss',
      ),
    ),
    el(
      'p',
      { class: 'text-secondary', style: 'font-size: 0.82rem; margin: 0;' },
      'Now that AI is connected, screen your saved jobs against your profile, or batch-generate documents for the ones you like.',
    ),
    el('div', { class: 'bulk-controls' }, screenBtn),
    el('div', { class: 'field-label', style: 'margin-top: 0.6rem;' }, 'Generate documents'),
    list,
    el('div', { class: 'bulk-controls' }, genBtn),
    status,
    errorBox,
  );

  return container;
}
